/**
 * In-memory stand-in for api-service's brand kit routes (distributionBrandKits.types.ts). It
 * behaves like the backend: writes answer 202 and report `pending` on the first poll, then `done`;
 * a duplicate live name is 409 `BRAND_KIT_NAME_TAKEN`; a logo whose key wasn't uploaded is 422
 * with one row error per logo; uploads refuse more than 2 MB (413) and anything but PNG or JPEG
 * (422). Re-exported from distributionsApi.mock.ts, so the API clients reach it the same way.
 */
import { MockDomainError, mockNotFoundError, mockValidationError } from "../mockDomainError";
import { MOCK_BRAND_KITS, MOCK_BRAND_TENANT } from "./distributions.mockData";
import {
  BRAND_KIT_ERROR_CODES,
  BRAND_KIT_LIMITS,
  type BrandKitAcceptedApi,
  type BrandKitApi,
  type BrandKitContentApi,
  type BrandKitListApi,
  type LogoUploadedApi,
} from "./distributionBrandKits.types";
import type { EditStatusApi, RowErrorDetailApi } from "./distributions.types";

const LATENCY_MS = process.env.NODE_ENV === "test" ? 0 : 250;
const wait = (): Promise<void> => (LATENCY_MS ? new Promise((resolve) => setTimeout(resolve, LATENCY_MS)) : Promise.resolve());
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const HEX = /^#[0-9a-f]{6}$/i;

let seq = 0;
const nextId = (prefix: string): string => `${prefix}-${(++seq).toString(36)}-${Date.now().toString(36)}`;

let kits: BrandKitApi[] = clone(MOCK_BRAND_KITS);
const edits = new Map<string, number>();
/** Uploaded files by key: their display URL. Seeded logos count as uploaded. */
let uploads = new Map<string, string | null>();
const seedUploads = (): void => {
  uploads = new Map(MOCK_BRAND_KITS.flatMap((kit) => kit.logos.map((logo) => [logo.object_key, logo.url] as const)));
};
seedUploads();

const byDefaultThenName = (a: BrandKitApi, b: BrandKitApi): number =>
  Number(b.is_default) - Number(a.is_default) || a.name.toLowerCase().localeCompare(b.name.toLowerCase());

const accept = (kitId: string): BrandKitAcceptedApi => {
  const requestId = nextId("kit-req");
  edits.set(requestId, 0);
  return { kit_id: kitId, request_id: requestId };
};

const findKit = (kitId: string): BrandKitApi => {
  const kit = kits.find((k) => k.id === kitId);
  if (!kit) throw mockNotFoundError("This brand kit doesn’t exist anymore. It may have been deleted.");
  return kit;
};

const validate = (body: BrandKitContentApi, selfId: string | null): void => {
  const name = body.name.trim();
  const details: RowErrorDetailApi[] = [];
  if (!name) details.push({ position: 0, field: "name", code: "required", message: "Add a name for the kit." });
  if (name.length > BRAND_KIT_LIMITS.nameMax) details.push({ position: 0, field: "name", code: "too_long", message: "Use 120 characters or fewer for the name." });
  if ((body.mark ?? "").length > BRAND_KIT_LIMITS.markMax) details.push({ position: 0, field: "mark", code: "too_long", message: "Use one or two letters for the mark." });
  if (body.logos.length > BRAND_KIT_LIMITS.maxLogos) details.push({ position: 0, field: "logos", code: "too_many", message: "A kit holds up to 10 logos." });
  if (body.palettes.length > BRAND_KIT_LIMITS.maxPalettes) details.push({ position: 0, field: "palettes", code: "too_many", message: "A kit holds up to 10 palettes." });
  body.palettes.forEach((palette) => {
    if (!palette.colors.length || palette.colors.length > BRAND_KIT_LIMITS.maxColors || !palette.colors.every((c) => HEX.test(c))) {
      details.push({ position: 0, field: "palettes", code: "invalid", message: `“${palette.name}” needs 1 to 12 colours written as #RRGGBB.` });
    }
  });
  if (details.length) throw mockValidationError(details[0].message, details);
  const logoErrors: RowErrorDetailApi[] = body.logos.flatMap((logo, index) =>
    uploads.has(logo.object_key)
      ? []
      : [{ position: index + 1, field: "logos.object_key", code: "logo_not_found", message: `We couldn’t find the file for “${logo.name}”. Upload it again.` }],
  );
  if (logoErrors.length) throw mockValidationError(logoErrors[0].message, logoErrors);
  const taken = kits.find((k) => k.id !== selfId && k.name.trim().toLowerCase() === name.toLowerCase());
  if (taken) {
    throw new MockDomainError({ status: 409, code: BRAND_KIT_ERROR_CODES.nameTaken, message: `There’s already a kit called “${taken.name}”. Use another name.` });
  }
};

const toKit = (id: string, body: BrandKitContentApi, isDefault: boolean): BrandKitApi => ({
  id,
  name: body.name.trim(),
  display_name: body.display_name?.trim() || null,
  mark: body.mark?.trim() || null,
  is_default: isDefault,
  logos: body.logos.map((logo) => ({ ...clone(logo), url: uploads.get(logo.object_key) ?? null })),
  palettes: clone(body.palettes),
  created_by: null,
  updated_at: new Date().toISOString(),
});

export const mockListBrandKits = async (): Promise<BrandKitListApi> => {
  await wait();
  return clone({ items: [...kits].sort(byDefaultThenName) });
};

export const mockGetBrandKit = async (kitId: string): Promise<BrandKitApi> => {
  await wait();
  return clone(findKit(kitId));
};

export const mockCreateBrandKit = async (body: BrandKitContentApi): Promise<BrandKitAcceptedApi> => {
  await wait();
  validate(body, null);
  const id = nextId("kit");
  kits.push(toKit(id, body, false));
  return accept(id);
};

export const mockUpdateBrandKit = async (kitId: string, body: BrandKitContentApi): Promise<BrandKitAcceptedApi> => {
  await wait();
  const kit = findKit(kitId);
  validate(body, kitId);
  kits = kits.map((k) => (k.id === kitId ? toKit(kitId, body, kit.is_default) : k));
  return accept(kitId);
};

/** Archives: a default kit leaves the tenant with no default. */
export const mockDeleteBrandKit = async (kitId: string): Promise<BrandKitAcceptedApi> => {
  await wait();
  findKit(kitId);
  kits = kits.filter((k) => k.id !== kitId);
  return accept(kitId);
};

export const mockSetDefaultBrandKit = async (kitId: string): Promise<BrandKitAcceptedApi> => {
  await wait();
  findKit(kitId);
  kits = kits.map((k) => ({ ...k, is_default: k.id === kitId }));
  return accept(kitId);
};

export const mockGetBrandKitEdit = async (requestId: string): Promise<EditStatusApi> => {
  await wait();
  const polls = edits.get(requestId);
  if (polls === undefined) throw mockNotFoundError("We couldn’t find that change. Refresh the page to see the latest brand kits.");
  edits.set(requestId, polls + 1);
  return { request_id: requestId, status: polls === 0 ? "pending" : "done", error: null };
};

const LOGO_TYPES = ["image/png", "image/jpeg"] as const;
type LogoType = (typeof LOGO_TYPES)[number];
const isLogoType = (type: string): type is LogoType => (LOGO_TYPES as readonly string[]).includes(type);

/** Mirrors api-service: the size first (413), then the type (422). The mock trusts `file.type`. */
export const mockUploadBrandLogo = async (file: Blob, fileName: string): Promise<LogoUploadedApi> => {
  await wait();
  if (file.size > BRAND_KIT_LIMITS.maxLogoBytes) {
    throw new MockDomainError({ status: 413, code: BRAND_KIT_ERROR_CODES.logoTooLarge, message: `“${fileName}” is larger than 2 MB. Export a smaller PNG, or upload the logo as SVG.` });
  }
  if (!isLogoType(file.type)) {
    throw new MockDomainError({ status: 422, code: BRAND_KIT_ERROR_CODES.logoNotImage, message: `“${fileName}” isn’t a PNG or JPEG image. Upload the logo as PNG, JPG or SVG.` });
  }
  const logoId = nextId("logo");
  const objectKey = `brand-kits/${MOCK_BRAND_TENANT}/logos/${logoId}.${file.type === "image/png" ? "png" : "jpg"}`;
  const url = typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null;
  uploads.set(objectKey, url);
  return { logo_id: logoId, object_key: objectKey, content_type: file.type, width: null, height: null, url };
};

/** Test and story helper: forget every write and reseed. */
export const resetBrandKitsMock = (): void => {
  kits = clone(MOCK_BRAND_KITS);
  edits.clear();
  seedUploads();
  seq = 0;
};
